import type { ParsedUrlQuery } from "querystring";
import { Message, Product, Variant } from "../../types/types";
import { getSizeQuery } from "../../utils/routing/getSizeQuery";
import { hasSizes } from "../../utils/product/hasSizes";
import { hasColours } from "../../utils/product/hasColours";
import {
  NonPromotionalMessage,
  PromotionalMessage,
} from "../../types/prodcatBaseTypes";

export function promotionalMessagesToShow(
  query: ParsedUrlQuery,
  product: Product,
  selectedVariant: Variant,
) {
  const sizeParam = getSizeQuery(query);

  const productHasSizes = hasSizes(product);
  const productHasColours = hasColours(product);

  const isVariantExplicitlySelected =
    (productHasSizes && sizeParam) || productHasColours;

  const isPromotionalMessage = (
    message: NonPromotionalMessage | PromotionalMessage,
  ): message is PromotionalMessage => {
    return (message as PromotionalMessage).promotionGroup !== undefined;
  };

  const variantPromotionalMessaging = selectedVariant.messaging
    ? selectedVariant.messaging.filter<PromotionalMessage>(isPromotionalMessage)
    : [];

  const productPromotionalMessaging = product.messaging
    ? product.messaging.filter<PromotionalMessage>(isPromotionalMessage)
    : [];

  const promotionalMessages =
    (isVariantExplicitlySelected && variantPromotionalMessaging.length
      ? variantPromotionalMessaging
      : productPromotionalMessaging) || [];

  return promotionalMessages;
}

export function usePreviousSaveMessaging(
  messaging: Message[],
  isPreviousMessaging: boolean,
) {
  if (!isPreviousMessaging) {
    return messaging;
  }

  return messaging.map((message) => {
    const previousSaveMessage = Object.assign({}, message);

    let savingValueString = "";
    let messageString = "";

    if (/^Save\s£\d+/.test(message.title)) {
      const saving = message.title.match(/^Save\s£(\d+\.\d+|\d+)/);
      savingValueString = `Save £${saving![1]}`;
    } else if (/^Save\s(\d+|\d+\.\d+)%/.test(message.title)) {
      const saving = message.title.match(/^Save\s(\d+\.\d+|\d+)%/);
      savingValueString = `Save ${saving![1]}%`;
    } else {
      return message;
    }

    if (/on\sselected\sproducts$/.test(message.title)) {
      messageString = "(Price includes saving on selected lines)";
    } else if (/until\s\d+\s[a-zA-Z]+\s\d+$/.test(message.title)) {
      const date = message.title.match(/until\s(\d+\s[a-zA-Z]+\s\d+)$/);
      const dateValue = date![1];

      if (/on\sselected\sproducts/.test(message.title)) {
        messageString = `(Offer ends ${dateValue} | Price includes saving on selected lines)`;
      } else {
        messageString = `(Offer ends ${dateValue} | Price includes saving)`;
      }
    } else if (
      /^Save\s£(\d+|\d+\.\d+)+$/.test(message.title) ||
      /^Save\s(\d+|\d+\.\d+)%$/.test(message.title)
    ) {
      messageString = "(Price includes saving)";
    } else {
      return message;
    }

    previousSaveMessage.title = `${savingValueString} ${messageString}`;

    return previousSaveMessage;
  });
}
